import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useAssets,
  useChecklistRun,
  useDeleteChecklistRun,
  usePatchChecklistRun,
  usePatchChecklistRunItem,
} from '@/lib/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ClipboardList, Minus, OctagonX, Plus, Trash2 } from 'lucide-react';
import { runProgressFrom, runStatusLabel } from '@/lib/checklist-helpers';
import { cn } from '@/lib/utils';
import type { ChecklistRunItem, ChecklistRunItemStatus } from '@/lib/types';
import ConfirmDialog from '@/components/ConfirmDialog';
import PhotoGrid from '@/components/PhotoGrid';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import { useAppShell } from '@/components/AppShellContext';

/**
 * Live checklist run. Designed for one-handed phone use:
 *  - Items are big tappable rows.
 *  - Status icons (check / skip / blocked) sit in the right gutter
 *    where the thumb naturally lands.
 *  - Notes save on blur — no "Save" round-trip.
 *  - The progress strip + status pill stay sticky at the top so the
 *    user always sees how close they are to done.
 */
export default function ChecklistRunPage() {
  const { id } = useParams<{ id: string }>();
  const q = useChecklistRun(id);
  const patchItem = usePatchChecklistRunItem(id ?? '');
  const patchRun = usePatchChecklistRun(id ?? '');
  const photos = useAssets(
    id ? { entityType: 'checklist_run', entityId: id, limit: 60 } : {},
  );
  const deleteRun = useDeleteChecklistRun();
  const navigate = useNavigate();
  const [abandonOpen, setAbandonOpen] = useState(false);
  const queryClient = useQueryClient();
  const { openLogForm, openPhotoSheet } = useAppShell();

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['checklist-run', id] });
  };

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;

  const { run, items, template } = q.data;
  const pct = runProgressFrom(q.data);
  const allClosed = items.every((i) => i.status !== 'open');

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6">
        <header className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Link to="/checklists" className="hover:text-foreground">
              Checklists
            </Link>{' '}
            <span aria-hidden>·</span> Run
            {template ? (
              <>
                {' '}<span aria-hidden>·</span>{' '}
                <Link to={`/checklists/templates/${template.slug}`} className="hover:text-foreground">
                  {template.slug}
                </Link>
              </>
            ) : null}
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            {run.title}
          </h1>
          <div className="flex items-center gap-3 pt-1">
            <Badge
              className={cn(
                'font-mono text-[10px] tracking-wider',
                run.status === 'completed' && 'bg-primary/15 text-primary border-primary/20',
                run.status === 'in_progress' && 'bg-warning/15 text-warning border-warning/20',
                (run.status === 'not_started' || run.status === 'archived' || run.status === 'skipped') &&
                  'bg-muted text-muted-foreground',
              )}
              variant="outline"
            >
              {runStatusLabel(run)}
            </Badge>
            <span className="font-mono text-xs text-muted-foreground tabular-nums font-tabular">
              {pct}% complete · {items.length} items
            </span>
          </div>
        </header>

        {/* Progress strip — sticky on mobile so it stays visible
            while scrolling the items. */}
        <div className="sticky top-14 z-20 -mx-4 px-4 py-2 bg-background/95 backdrop-blur md:static md:mx-0 md:px-0">
          <div
            data-testid="run-progress"
            data-progress={pct}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
          >
            <div
              className="h-full bg-primary transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <section
          className="divide-y divide-rule rounded-md border border-rule bg-card"
          data-testid="run-items"
        >
          {items.map((it, ix) => (
            <RunItemRow
              key={it.id}
              index={ix + 1}
              item={it}
              onSetStatus={(status) =>
                patchItem.mutate({ itemId: it.id, patch: { status } })
              }
              onSetNotes={(notes) =>
                patchItem.mutate({ itemId: it.id, patch: { notes } })
              }
            />
          ))}
        </section>

        <Card>
          <CardHeader className="space-y-1 pb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Run notes
            </p>
            <CardTitle className="font-display text-base">Free-form notes</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <RunNotesEditor
              initial={run.notes ?? ''}
              onSave={(notes) => patchRun.mutate({ notes })}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
            <div className="min-w-0 space-y-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Photos
              </p>
              <CardTitle className="font-display text-base">
                Evidence
                {photos.data && photos.data.length > 0 ? (
                  <span className="ml-2 font-mono text-xs text-muted-foreground tabular-nums font-tabular">
                    · {String(photos.data.length).padStart(2, '0')}
                  </span>
                ) : null}
              </CardTitle>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-9 shrink-0 sm:h-10"
              onClick={() =>
                openPhotoSheet({
                  domainId: template?.domainId ?? null,
                  entityType: 'checklist_run',
                  entityId: run.id,
                  tags: template ? ['run', template.slug] : ['run'],
                })
              }
              data-testid="run-add-photo"
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden /> Photo
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {photos.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <PhotoGrid
                assets={photos.data ?? []}
                emptyMessage="No photos yet — tap + Photo to start the evidence trail."
              />
            )}
          </CardContent>
        </Card>

        {/*
          Action row. Two groups separated visually by intent:
            • constructive pair (Log this run + Mark complete) on the
              right, where the thumb naturally lands on a phone.
            • destructive Abandon on the left (desktop) / bottom
              (mobile), one tap away from the rest of the row so it's
              not accidentally pressed mid-tick.
        */}
        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center order-2 sm:order-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAbandonOpen(true)}
              className="h-11 text-base text-destructive hover:bg-destructive/10 hover:text-destructive"
              data-testid="abandon-run"
            >
              <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Abandon run
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center order-1 sm:order-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                openLogForm({
                  domainId: template?.domainId,
                  entityType: 'checklist_run',
                  entityId: run.id,
                  title: `Ran: ${run.title}`,
                })
              }
              className="h-11 text-base"
              data-testid="log-this-run"
            >
              <ClipboardList className="h-4 w-4 mr-2" aria-hidden /> Log this run
            </Button>
            {run.status !== 'completed' ? (
              <Button
                type="button"
                onClick={() => patchRun.mutate({ status: 'completed' })}
                disabled={!allClosed || patchRun.isPending}
                className="h-11 text-base"
                data-testid="mark-complete"
              >
                {patchRun.isPending ? 'Saving…' : 'Mark run complete'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={abandonOpen}
        onOpenChange={setAbandonOpen}
        title="Abandon this run?"
        description="The run and its items will be deleted. The template stays. Photos and logs you tagged to this run remain in their normal places — only the run itself goes."
        confirmLabel="Abandon run"
        busyLabel="Abandoning…"
        tone="destructive"
        testId="confirm-abandon"
        onConfirm={async () => {
          await deleteRun.mutateAsync(run.id);
          navigate('/checklists');
        }}
      />
    </PullToRefresh>
  );
}

function RunItemRow({
  index,
  item,
  onSetStatus,
  onSetNotes,
}: {
  index: number;
  item: ChecklistRunItem;
  onSetStatus: (status: ChecklistRunItemStatus) => void;
  onSetNotes: (notes: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(item.notes ?? '');
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => setNotes(item.notes ?? ''), [item.notes]);

  const setStatus = (status: ChecklistRunItemStatus) => {
    // Toggle off if the user taps the active status — sets back to
    // open. This is the dominant "I tapped done by mistake" recovery.
    onSetStatus(item.status === status ? 'open' : status);
  };

  return (
    <div
      className={cn(
        'px-4 py-3',
        item.status === 'done' && 'bg-primary/5',
        item.status === 'blocked' && 'bg-warning/10',
      )}
      data-testid={`run-item-${item.id}`}
      data-status={item.status}
    >
      <div className="flex items-start gap-3">
        <span className="font-mono text-[11px] tabular-nums uppercase tracking-wider text-muted-foreground pt-2 w-6 shrink-0">
          {String(index).padStart(2, '0')}
        </span>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className={cn(
              'text-left w-full py-1',
              item.status === 'done' && 'line-through text-foreground/60',
              item.status === 'skipped' && 'text-foreground/50',
              item.status === 'blocked' && 'text-warning',
            )}
          >
            {item.label}
          </button>
          {(open || item.notes) ? (
            <textarea
              ref={ref}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => onSetNotes(notes.trim() === '' ? null : notes)}
              placeholder="Note…"
              rows={2}
              className="mt-1.5 w-full rounded-md border border-rule bg-background px-2.5 py-1.5 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          ) : null}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <StatusButton
            label="Skip"
            active={item.status === 'skipped'}
            tone="muted"
            onClick={() => setStatus('skipped')}
            Icon={Minus}
            testId={`skip-${item.id}`}
          />
          <StatusButton
            label="Blocked"
            active={item.status === 'blocked'}
            tone="warning"
            onClick={() => setStatus('blocked')}
            Icon={OctagonX}
            testId={`block-${item.id}`}
          />
          <StatusButton
            label="Done"
            active={item.status === 'done'}
            tone="primary"
            onClick={() => setStatus('done')}
            Icon={Check}
            testId={`done-${item.id}`}
          />
        </div>
      </div>
    </div>
  );
}

function StatusButton({
  label,
  active,
  tone,
  onClick,
  Icon,
  testId,
}: {
  label: string;
  active: boolean;
  tone: 'primary' | 'warning' | 'muted';
  onClick: () => void;
  Icon: typeof Check;
  testId: string;
}) {
  const toneActive =
    tone === 'primary'
      ? 'bg-primary text-primary-foreground border-primary'
      : tone === 'warning'
        ? 'bg-warning text-warning-foreground border-warning'
        : 'bg-muted text-foreground border-border';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-md border transition-colors',
        active ? toneActive : 'border-rule text-muted-foreground hover:text-foreground hover:bg-muted',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

function RunNotesEditor({
  initial,
  onSave,
}: {
  initial: string;
  onSave: (notes: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return (
    <textarea
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const next = value.trim();
        onSave(next === '' ? null : next);
      }}
      placeholder="What happened? Anything to remember next time?"
      rows={4}
      className="w-full rounded-md border border-rule bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
    />
  );
}
